import React, { useState } from "react";
import { titleCase } from "title-case";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import blank from "../../assets/images/blank.webp";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { useColorScheme } from "context/ColorSchemeContext";

const API_KEY = process.env.REACT_APP_BRAND_FETCH_API_KEY;

interface MerchantData {
  logo?: string;
  cachedLogo?: string;
  url?: string;
  merchant: string;
  logoType?: string;
  fallback?: string;
  /** Brandfetch logo variant. Defaults to the viewer's colour scheme. */
  theme?: string;
  height?: number;
  width?: number;
  withTitle?: boolean;
  isCircular?: boolean;
}

interface LogoFromTextProps {
  merchant: string;
  fallback: string;
  height?: number;
  width?: number;
  logoType?: string;
  isCircular?: boolean;
}

export const logoLink = (
  domain: string,
  logoType: string,
  fallback: string,
  theme: string,
  width: number,
  height?: number,
): string => {
  let heightStr = "";
  if (height) {
    heightStr = `/h/${height}`;
  }
  return `https://cdn.brandfetch.io/${domain}/theme/${theme}/fallback/${fallback}/w/${width}${heightStr}/${logoType}?c=${API_KEY}`;
};

export const formatUrl = (url: string): string => {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
};

export const getDomain = (url: string): string => {
  try {
    const formattedUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(formattedUrl).hostname;
  } catch (error) {
    console.error("Invalid URL:", error);
    return "";
  }
};

/**
 * The logo box. `--radius-sm` rather than square corners, matching every other
 * avatar surface; `isCircular` still wins where a call site asks for a round
 * mark.
 */
const logoBoxStyle = (
  logoType: string,
  isCircular: boolean,
  width?: number,
  height?: number,
): React.CSSProperties => {
  const radius = isCircular ? "50%" : "var(--radius-sm)";

  if (logoType === "icon") {
    return {
      height: "24px",
      width: "24px",
      minWidth: "24px",
      borderRadius: radius,
    };
  }

  return {
    height: height ? `${height}px` : undefined,
    width: width ? `${width}px` : undefined,
    minWidth: width ? `${width}px` : undefined,
    borderRadius: radius,
  };
};

const LogoFromText: React.FC<LogoFromTextProps> = ({
  merchant,
  fallback,
  height,
  width,
  logoType,
  isCircular,
}) => {
  const getInitial = (str: string): string =>
    str ? str.charAt(0).toUpperCase() : "?";

  // Scales the initial to whatever box the call site asked for, so this size is
  // a measurement rather than a type choice.
  const fontSize = height
    ? `${Math.floor(height / 2)}px`
    : width
      ? `${Math.floor(width / 2)}px`
      : "12px";

  if (fallback !== "lettermark") return null;

  return (
    <Typography
      variant="subtitle2"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-elevated)",
        ...logoBoxStyle(logoType ?? "icon", isCircular ?? false, width, height),
        ...(logoType === "icon" ? {} : { fontSize }),
      }}
    >
      {getInitial(merchant)}
    </Typography>
  );
};

const ELLIPSIS = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const RenderMerchantDisplay: React.FC<
  MerchantData & { withLink?: boolean }
> = ({
  merchant,
  url,
  logo,
  cachedLogo,
  logoType = "icon",
  fallback = "lettermark",
  theme,
  height,
  width = 32,
  withTitle = true,
  withLink = true,
  isCircular = false,
}) => {
  const [error, setError] = useState(false);
  const { isDark } = useColorScheme();

  if (!merchant) return null;

  // Brandfetch serves a per-scheme logo variant, so a fixed "light" here is a
  // dark-mode break rather than a default.
  const brandTheme = theme ?? (isDark ? "dark" : "light");
  const style = logoBoxStyle(logoType, isCircular, width, height);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    if (cachedLogo) {
      target.src = cachedLogo;
    } else {
      setError(true);
      target.src = blank;
    }
  };

  const name = titleCase((merchant || "").toLowerCase());

  return (
    <Inline gap="var(--space-2)" wrap={false} sx={{ minWidth: 0 }}>
      {!error && (logo || url) ? (
        <img
          alt="company logo"
          style={{ ...style, border: 0 }}
          src={
            logo
              ? `${logo}?c=${API_KEY}`
              : url
                ? logoLink(
                    getDomain(url),
                    logoType,
                    fallback,
                    brandTheme,
                    width,
                    height,
                  )
                : blank
          }
          onError={handleError}
        />
      ) : (
        <LogoFromText
          merchant={merchant}
          fallback={fallback}
          height={height}
          width={width}
          logoType={logoType}
          isCircular={isCircular}
        />
      )}

      {withTitle &&
        (url && withLink ? (
          <Inline
            gap="var(--space-1)"
            wrap={false}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography variant="body1" sx={ELLIPSIS}>
              {name}
            </Typography>
            <Link
              href={formatUrl(url)}
              target="_blank"
              rel="noopener noreferrer"
              // Without this the link also opens the row drawer it sits inside.
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "inline-flex",
                color: "var(--color-text-secondary)",
                "&:hover": { color: "var(--color-text-brand)" },
              }}
            >
              <HugeiconsIcon icon={Link01Icon} size={16} />
            </Link>
          </Inline>
        ) : (
          <Typography variant="body1" sx={{ ...ELLIPSIS, flex: 1 }}>
            {name}
          </Typography>
        ))}
    </Inline>
  );
};

export default RenderMerchantDisplay;
